import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import FieldLabel from '../components/FieldLabel';
import { blockInvalidNumberKeys, sanitizeNumberInput } from '../utils/formHelpers';
import { outlinedFieldProps, disabledOutlinedSx } from '../utils/fieldStyles';

const HssSelectField = ({
    field,
    data,
    change,
    basePrice,
    hssTemplates = [],
    onNewHssTemplate,
}) => {
    const selectedTemplateId = data[`${field.key}_template`] || 'custom';
    const isCustom = selectedTemplateId === 'custom';
    const selectedTemplate = (hssTemplates || []).find((t) => t.id === selectedTemplateId);

    const hssPercentage = !isCustom && selectedTemplate
        ? selectedTemplate.hss_percentage
        : (data[`${field.key}_percentage`] || '');

    const additionalCharge = parseFloat(data[`${field.key}_flat_charge`] || 0);
    const base = parseFloat(basePrice || 0);

    const calculatedTotal = hssPercentage
        ? ((base * parseFloat(hssPercentage) / 100) + additionalCharge).toFixed(2)
        : '';

    const handleTemplateChange = (templateId) => {
        if (templateId === 'custom') {
            change({
                [`${field.key}_template`]: templateId,
                [`${field.key}_percentage`]: '',
                [`${field.key}_flat_charge`]: '',
            });
        } else {
            const tpl = (hssTemplates || []).find((t) => t.id === templateId);
            change({
                [`${field.key}_template`]: templateId,
                [`${field.key}_percentage`]: tpl ? tpl.hss_percentage : '',
                [`${field.key}_flat_charge`]: '',
            });
        }
    };

    return (
        <Box sx={{ pt: 1 }}>
            <MDTypography variant="subtitle2" fontWeight="medium" mb={2} display="flex" alignItems="center">
                {field.label}
                {field.tooltip && (
                    <Tooltip title={field.tooltip} arrow placement="top">
                        <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                )}
            </MDTypography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <TextField
                    {...outlinedFieldProps}
                    select
                    label={<FieldLabel label="HSS Template" tooltip={field.tooltips?.template} />}
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                >
                    <MenuItem value="custom">Custom / None</MenuItem>
                    {(hssTemplates || []).map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.template_name}</MenuItem>
                    ))}
                </TextField>
                {typeof onNewHssTemplate === 'function' && (
                    <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => onNewHssTemplate((created) => {
                            if (!created?.id) return;
                            change({
                                [`${field.key}_template`]: created.id,
                                [`${field.key}_percentage`]: created.hss_percentage ?? '',
                                [`${field.key}_flat_charge`]: '',
                            });
                        })}
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: 40 }}
                    >
                        Add
                    </MDButton>
                )}
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        label={<FieldLabel label="HSS Percentage (%)" tooltip={field.tooltips?.percentage} />}
                        type="number"
                        value={hssPercentage}
                        onKeyDown={blockInvalidNumberKeys}
                        onChange={(e) => change(`${field.key}_percentage`, sanitizeNumberInput(e.target.value))}
                        disabled={!isCustom}
                        sx={!isCustom ? disabledOutlinedSx : undefined}
                        inputProps={{ inputMode: 'decimal' }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        label={<FieldLabel label="Additional Charge (Flat)" tooltip={field.tooltips?.flatCharge} />}
                        type="number"
                        value={data[`${field.key}_flat_charge`] || ''}
                        onKeyDown={blockInvalidNumberKeys}
                        onChange={(e) => {
                            const flat = sanitizeNumberInput(e.target.value);
                            const newTotal = hssPercentage
                                ? ((base * parseFloat(hssPercentage) / 100) + parseFloat(flat || 0)).toFixed(2)
                                : '';
                            change({
                                [`${field.key}_flat_charge`]: flat,
                                [`${field.key}_total`]: newTotal,
                            });
                        }}
                        inputProps={{ inputMode: 'decimal' }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        label={<FieldLabel label="HSS Amount" tooltip={field.tooltips?.amount} />}
                        value={calculatedTotal}
                        disabled
                        sx={disabledOutlinedSx}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default HssSelectField;
